/**
 * @description Website page — CMS provisioning status, site links, domain connect (LYN-21, LYN-20)
 *
 * Key features:
 * - Shows cms_sites status for the active company or SO self-site context
 * - View Site (public hostname) and Edit Site (Payload /admin SSO hop)
 * - Connect Domain flow with DNS instructions and verification polling
 *
 * @dependencies
 * - useCmsSite: CMS access + site status from /api/features/*
 * - ConnectDomainPanel: custom domain UX (LYN-20)
 * - PermissionGuard + cms.view: same RBAC as Website nav (not company.view)
 *
 * @notes
 * Deep links without cms.view show the existing access-denied guard,
 * not "Unable to load website status". Employees with company.view still
 * cannot open Website unless cms.view is granted. useCmsSite skips the
 * status fetch until a company exists.
 * payloadTenantId is internal (Payload / KV) — do not show "CMS tenant" to merchants.
 * Do not mention Payload or Wristband in merchant-facing Website copy.
 */

import React from 'react';
import {
  Globe,
  ExternalLink,
  Pencil,
  AlertCircle,
  RefreshCw,
  Link2,
  CheckCircle2,
  Clock,
  Ban,
  Sparkles,
  Loader2
} from 'lucide-react';
import useCmsSite from '../../hooks/useCmsSite';
import ConnectDomainPanel from './ConnectDomainPanel';
import { PermissionGuard } from '../shared/PermissionGate';
import { PERMISSIONS } from '../../constants/permissionMappings';

const CMS_ACCESS_DENIED_MESSAGE = "Your account doesn't have access to Website features.";

const STATUS_STYLES = {
  active: {
    label: 'Active',
    className: 'bg-green-100 text-green-800',
    dotClass: 'bg-green-500',
    icon: CheckCircle2
  },
  provisioning: {
    label: 'Provisioning',
    className: 'bg-blue-100 text-blue-800',
    dotClass: 'bg-blue-500',
    icon: Clock
  },
  suspended: {
    label: 'Suspended',
    className: 'bg-yellow-100 text-yellow-800',
    dotClass: 'bg-yellow-500',
    icon: Ban
  },
  archived: {
    label: 'Archived',
    className: 'bg-gray-100 text-gray-800',
    dotClass: 'bg-gray-500',
    icon: Ban
  }
};

const StatusBadge = ({ status }) => {
  const config = STATUS_STYLES[status] || {
    label: status || 'Unknown',
    className: 'bg-gray-100 text-gray-800',
    dotClass: 'bg-gray-400',
    icon: AlertCircle
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${config.dotClass}`} />
      {config.label}
    </span>
  );
};

const FREE_TIER_DISCLAIMERS = [
  'Your site lives on a platform subdomain until you connect a custom domain.',
  'Free includes basic pages and content tools — advanced features may require an upgrade later.',
  'Platform branding appears on the free tier.'
];

const WebsitePage = () => {
  const {
    loading,
    error,
    siteStatus,
    provisioning,
    provisionError,
    refresh,
    provision,
    domainStatus,
    domainLoading,
    domainError,
    connectingDomain,
    connectDomainError,
    disconnectingDomain,
    disconnectDomainError,
    refreshDomainStatus,
    connectDomain,
    disconnectDomain
  } = useCmsSite();

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6 max-w-2xl">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900">Unable to load website status</h3>
              <p className="text-sm text-gray-600 mt-1">{error}</p>
              <button
                type="button"
                onClick={refresh}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (!siteStatus?.access?.allowed) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-2xl">
          <div className="flex items-start gap-3">
            <Ban className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">Website not available</h3>
              <p className="text-sm text-gray-600 mt-1">
                {siteStatus?.access?.reason || CMS_ACCESS_DENIED_MESSAGE}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (!siteStatus.provisioned) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 max-w-2xl">
          <div className="flex items-start gap-4">
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-3 flex-shrink-0">
              <Globe className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900">Build your website</h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                Launch your site in minutes. No credit card required — explore the
                editor and publish when you are ready.
              </p>

              {provisionError && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{provisionError}</span>
                </div>
              )}

              <button
                type="button"
                onClick={provision}
                disabled={provisioning}
                className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {provisioning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Setting up…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Start for free
                  </>
                )}
              </button>

              <ul className="mt-6 space-y-2 text-xs text-gray-500">
                {FREE_TIER_DISCLAIMERS.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-gray-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      );
    }

    const { site, urls } = siteStatus;
    const editSiteUnavailable = siteStatus.error?.code === 'CMS_SSO_UNAVAILABLE'
      || !urls?.editSiteUrl
      || !urls?.editSiteAvailable;
    const editSiteErrorMessage = siteStatus.error?.message
      || urls?.editSiteUnavailableReason
      || 'Website editor sign-in is not available.';
    const publicHostname = urls?.usesCustomDomain
      ? site.customHostname
      : site.defaultHostname;

    return (
      <div className="space-y-6 max-w-3xl">
        {editSiteUnavailable && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900">Edit Site unavailable</h3>
              <p className="text-sm text-amber-800 mt-1">{editSiteErrorMessage}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-3 shadow-md flex-shrink-0">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Your Website</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Create, edit and manage your online presence.
                </p>
                <div className="mt-3">
                  <StatusBadge status={site.status} />
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <a
                href={urls.siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                <ExternalLink className="h-4 w-4" />
                View Site
              </a>
              {!editSiteUnavailable && (
                <a
                  href={urls.editSiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors font-medium text-sm"
                >
                  <Pencil className="h-4 w-4" />
                  Edit Site
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Site details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div>
              <dt className="font-medium text-gray-600">Public URL</dt>
              <dd className="mt-1 flex items-center gap-2 text-gray-900">
                <Link2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <a
                  href={urls.siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand hover:underline break-all"
                >
                  {urls.siteUrl.replace(/^https:\/\//, '')}
                </a>
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-600">Default hostname</dt>
              <dd className="mt-1 font-mono text-gray-900 break-all">{publicHostname}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-600">Site slug</dt>
              <dd className="mt-1 font-mono text-gray-900">{site.slug}</dd>
            </div>
            {site.customHostname && (
              <div className="sm:col-span-2">
                <dt className="font-medium text-gray-600">Custom domain</dt>
                <dd className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="font-mono text-gray-900">{site.customHostname}</span>
                  <StatusBadge status={site.customHostnameStatus === 'verified' ? 'active' : 'provisioning'} />
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <ConnectDomainPanel
            defaultHostname={site.defaultHostname}
            domainStatus={domainStatus}
            loading={domainLoading}
            error={domainError}
            connectError={connectDomainError}
            connecting={connectingDomain}
            disconnectError={disconnectDomainError}
            disconnecting={disconnectingDomain}
            onRefresh={refreshDomainStatus}
            onConnect={connectDomain}
            onDisconnect={disconnectDomain}
          />
        </div>
      </div>
    );
  };

  return (
    <PermissionGuard
      permission={PERMISSIONS.cms.view}
      message="You don't have permission to access Website."
    >
      <div className="min-h-screen">
        <div className="max-w-[2000px] w-full mx-auto px-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Website</h1>
          </div>
          {renderContent()}
        </div>
      </div>
    </PermissionGuard>
  );
};

export default WebsitePage;
